import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellRing, CheckCheck, Moon, Volume2, VolumeX, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NOTIFICATION_META, notificationsApi, type AppNotification } from '@/api/notifications.api';
import {
  currentPushSubscription,
  disablePush,
  enablePush,
  pushPermission,
  pushSupported,
  subscriptionBody,
} from '@/lib/push';
import { playChime } from '@/lib/chime';
import './NotificationBell.css';
import { plural } from '@/lib/format';
import { useTranslation } from 'react-i18next';

/** Notificările în browser cer doar API-ul `Notification`, nu și push/VAPID. */
type OsNotifState = 'unsupported' | NotificationPermission;
function osNotifPermission(): OsNotifState {
  return typeof window !== 'undefined' && 'Notification' in window
    ? Notification.permission
    : 'unsupported';
}

/** Ține minte, per dispozitiv, dacă sunetul e oprit. */
function readMuted(): boolean {
  try {
    return localStorage.getItem('nb-sound') === 'off';
  } catch {
    return false;
  }
}
function writeMuted(muted: boolean): void {
  try {
    localStorage.setItem('nb-sound', muted ? 'off' : 'on');
  } catch {
    // Un browser fără localStorage (fereastră privată) păstrează alegerea doar
    // pe durata sesiunii — starea din React o ține oricum.
  }
}

/**
 * Orele de liniște, per dispozitiv: în intervalul ales nu se aude sunetul și nu
 * apare notificarea de sistem — badge-ul și lista rămân, ca medicul să vadă la
 * loc liniștit ce s-a strâns. Gândit pentru timpul consultației.
 */
interface Quiet {
  on: boolean;
  from: string;
  to: string;
}
const DEFAULT_QUIET: Quiet = { on: false, from: '20:00', to: '08:00' };
function readQuiet(): Quiet {
  try {
    const raw = localStorage.getItem('nb-quiet');
    if (!raw) return DEFAULT_QUIET;
    const p = JSON.parse(raw) as Partial<Quiet>;
    return {
      on: p.on === true,
      from: typeof p.from === 'string' ? p.from : DEFAULT_QUIET.from,
      to: typeof p.to === 'string' ? p.to : DEFAULT_QUIET.to,
    };
  } catch {
    return DEFAULT_QUIET;
  }
}
function writeQuiet(q: Quiet): void {
  try {
    localStorage.setItem('nb-quiet', JSON.stringify(q));
  } catch {
    // Ca la sunet: fără localStorage, alegerea ține cât sesiunea.
  }
}
/** Suntem acum în intervalul de liniște? Gestionează trecerea peste miezul nopții. */
function inQuietHours(q: Quiet, now = new Date()): boolean {
  if (!q.on) return false;
  const [fh, fm] = q.from.split(':').map(Number);
  const [th, tm] = q.to.split(':').map(Number);
  const from = (fh ?? 0) * 60 + (fm ?? 0);
  const to = (th ?? 0) * 60 + (tm ?? 0);
  if (from === to) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  return from < to ? cur >= from && cur < to : cur >= from || cur < to;
}

/** Notificările pe categorii, categoria cu cea mai nouă notificare prima. */
function grouped(list: AppNotification[]): Array<{
  label: string;
  icon: string;
  items: AppNotification[];
  unreadInCat: number;
}> {
  const by = new Map<string, AppNotification[]>();
  for (const n of list) {
    // Lista vine deja de la nou la vechi, deci fiecare grup păstrează ordinea.
    const label = NOTIFICATION_META[n.type ?? '']?.label ?? 'Altele';
    const arr = by.get(label);
    if (arr) arr.push(n);
    else by.set(label, [n]);
  }
  return [...by.entries()]
    .map(([label, items]) => ({
      label,
      icon: NOTIFICATION_META[items[0]!.type ?? '']?.icon ?? '🔔',
      items,
      unreadInCat: items.filter((n) => !n.read).length,
    }))
    .sort(
      (a, b) =>
        new Date(b.items[0]!.createdAt).getTime() - new Date(a.items[0]!.createdAt).getTime(),
    );
}

/** "acum 3 ore", the way the rest of the app says it. */
function ago(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'acum';
  if (mins < 60) return `acum ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `acum ${plural(hours, 'oră', 'ore')}`;
  const days = Math.round(hours / 24);
  return `acum ${plural(days, 'zi', 'zile')}`;
}

/**
 * The bell.
 *
 * The notifications module has existed since the port — a list scoped to the
 * caller, read marks per person, a mark-everything-read — and nothing in the
 * interface ever called it. Stock fell below its minimum, a laboratory case
 * ran past its promised date, a patient asked for an appointment through the
 * portal, and the only way to find out was to open the screen it happened on.
 */
export function NotificationBell() {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    // La 20 de secunde, și în fundal: sunetul și alerta pentru o notificare
    // nouă vin din următoarea reîmprospătare, deci una prea rară le întârzie.
    // (Browserul încetinește oricum cronometrele unei file ascunse la ~1/min,
    // dar când reîmprospătează, alerta pleacă.)
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });

  const unread = (q.data ?? []).filter((n) => !n.read).length;

  /**
   * Ca medicul să nu rateze o notificare cu fila deschisă: la una nouă se aude
   * un sunet și, dacă a dat voie, apare o notificare de sistem — plus un mesaj
   * scurt în aplicație. „Doar în tab", cum a fost ales; push-ul cu fila închisă
   * rămâne separat (mai jos), unde platforma are chei.
   */
  const [muted, setMuted] = useState(readMuted);
  const [quiet, setQuiet] = useState<Quiet>(readQuiet);
  const [osPerm, setOsPerm] = useState<OsNotifState>(osNotifPermission);
  const [toast, setToast] = useState<{ id: string; text: string; link: string | null } | null>(
    null,
  );
  // Id-urile deja văzute (citite sau nu). `null` până la prima încărcare, ca
  // sosirea inițială a listei să nu sune pentru notificări deja acolo.
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    const list = q.data;
    if (!list) return;
    const ids = list.map((n) => n.id);
    if (seen.current === null) {
      seen.current = new Set(ids);
      return;
    }
    const fresh = list.filter((n) => !n.read && !seen.current!.has(n.id));
    seen.current = new Set(ids);
    if (fresh.length === 0) return;

    // Citit proaspăt din localStorage, nu din starea închisă în efect: efectul
    // depinde doar de listă, deci n-ar vedea o schimbare de sunet sau de ore de
    // liniște făcută între timp. Liniștea taie și sunetul, și notificarea de
    // sistem; „mut" taie doar sunetul. Toastul și badge-ul rămân mereu.
    const quietNow = inQuietHours(readQuiet());
    // O urgență (un finding fără plan, semnat) sună diferit, ca să se știe după
    // sunet că merită întrerupt.
    const urgent = fresh.some((n) => (n.type ?? '').includes('urgent'));
    if (!readMuted() && !quietNow) playChime(urgent ? 'urgent' : 'normal');

    const newest = fresh[0]!;
    setToast({ id: newest.id, text: newest.text, link: newest.link });

    // O notificare de sistem doar când fila nu e în față — altfel mesajul din
    // aplicație e de ajuns și una de sistem peste el supără.
    if (
      !quietNow &&
      osNotifPermission() === 'granted' &&
      typeof document !== 'undefined' &&
      document.hidden
    ) {
      try {
        const body = fresh.length > 1 ? `${fresh.length} notificări noi` : newest.text;
        const osn = new Notification('DentFlow', { body, tag: newest.id });
        osn.onclick = () => {
          window.focus();
          if (newest.link) navigate(newest.link);
          osn.close();
        };
      } catch {
        // Unele browsere cer notificările doar din service worker; sunetul și
        // mesajul din aplicație rămân.
      }
    }
  }, [q.data, navigate]);

  // Mesajul din aplicație se stinge singur; nu e un jurnal, e un semnal.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6500);
    return () => clearTimeout(t);
  }, [toast]);

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /**
   * Dismissing one — for the person who pressed it, not the whole clinic.
   *
   * Marking read greys a notification and leaves it in the list; the list is
   * what someone opens to see what still wants attention, and a read one that
   * will never be acted on is just in the way. This used to call the
   * clinic-wide delete, which needed `notificari.trimite` — a doctor, an
   * assistant, reception or the accountant got a silent 403 and the
   * notification stayed — and removed it for everyone who could see it, not
   * only whoever pressed „Elimină" (NOT-9). `dismiss` writes a per-person
   * mark instead, open to anyone who can see the notification in the first
   * place.
   */
  const dismiss = useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Close on a click outside or on Escape: a panel that can only be dismissed
  // by the button that opened it traps whoever opened it by accident.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /**
   * Push, for the browser this is.
   *
   * The bell polls while a tab is open; this is what reaches somebody whose
   * tab is closed. The switch appears only where the browser can do it and
   * the server has a key. On every load an existing subscription is sent to
   * the API again, so a browser that signed in as somebody else since then
   * follows the person now using it.
   */
  const [perm, setPerm] = useState(pushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const keyQ = useQuery({
    queryKey: ['push-key'],
    queryFn: notificationsApi.pushKey,
    staleTime: Infinity,
    enabled: pushSupported(),
  });
  const publicKey = keyQ.data?.publicKey ?? null;

  useEffect(() => {
    if (perm !== 'granted') return;
    let cancelled = false;
    void currentPushSubscription().then(async (sub) => {
      if (cancelled || !sub) return;
      setSubscribed(true);
      await notificationsApi.pushSubscribe(subscriptionBody(sub)).catch(() => undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [perm]);

  // A push that was clicked while this tab was open: the worker focuses the
  // tab and says where to go, so the click lands here instead of reloading.
  useEffect(() => {
    if (!pushSupported()) return;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; link?: string } | null;
      if (data?.type === 'open' && data.link) navigate(data.link);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  const enable = useMutation({
    mutationFn: async () => {
      if (!publicKey) return;
      const sub = await enablePush(publicKey);
      await notificationsApi.pushSubscribe(subscriptionBody(sub));
    },
    onSettled: () => setPerm(pushPermission()),
    onSuccess: () => setSubscribed(true),
  });
  const disable = useMutation({
    mutationFn: async () => {
      const endpoint = await disablePush();
      if (endpoint) await notificationsApi.pushUnsubscribe(endpoint);
    },
    onSuccess: () => setSubscribed(false),
  });

  /*
   * „A ajuns?" — întrebarea de imediat după ce ai pornit notificările.
   *
   * Serverul avea de mult ruta care trimite un push către propriile browsere;
   * nimic nu o chema, deci răspunsul era „așteaptă să se întâmple ceva". Se
   * trimite numai către dispozitivele mele, deci nu deranjează pe nimeni.
   */
  const [testNote, setTestNote] = useState<string | null>(null);
  const test = useMutation({
    mutationFn: notificationsApi.pushTest,
    onSuccess: (r) =>
      setTestNote(
        r.sent > 0
          ? `Trimis către ${t('dispozitive', { count: r.sent })}.`
          : 'Niciun dispozitiv înregistrat pe contul tău.',
      ),
    onError: () => setTestNote('Testul nu a putut fi trimis.'),
  });

  const go = (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="nb" ref={wrap}>
      <button
        type="button"
        className={`nb__btn ${unread > 0 ? 'is-unread' : ''}`}
        aria-label={unread > 0 ? `Notificări · ${unread} necitite` : t('notificari')}
        aria-expanded={open}
        title={t('notificari')}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell size={16} />
        {unread > 0 ? <span className="nb__dot">{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {toast ? (
        <div className="nb__toast" role="status">
          <button
            type="button"
            className="nb__toastmain"
            onClick={() => {
              markRead.mutate(toast.id);
              if (toast.link) navigate(toast.link);
              setToast(null);
            }}
          >
            <span className="nb__toasticon" aria-hidden>
              <BellRing size={15} />
            </span>
            <span className="nb__toasttext">{toast.text}</span>
          </button>
          <button
            type="button"
            className="nb__toastclose"
            aria-label={t('inchide')}
            onClick={() => setToast(null)}
          >
            <X size={13} />
          </button>
        </div>
      ) : null}

      {open ? (
        <div className="nb__panel" role="dialog" aria-label={t('notificari')}>
          <header>
            <strong>{t('notificari')}</strong>
            {unread > 0 ? (
              <button type="button" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
                <CheckCheck size={13} /> {t('marcheazaToate')}
              </button>
            ) : null}
          </header>

          {q.isPending ? (
            <p className="nb__note">{t('seIncarca')}</p>
          ) : (q.data ?? []).length === 0 ? (
            <p className="nb__note">{t('nimicNouCandCeva')}</p>
          ) : (
            /* Grupate pe categorie, ca într-o listă lungă (stoc, laborator,
               imagistică…) să se vadă dintr-o privire ce fel de lucruri cer
               atenție. Categoriile sunt ordonate după cea mai nouă notificare a
               lor, deci cea mai recentă rămâne sus; înăuntru, tot noul întâi. */
            grouped(q.data!).map(({ label, icon, items, unreadInCat }) => (
              <section className="nb__group" key={label}>
                <div className="nb__grouphead">
                  <span className="nb__groupicon" aria-hidden>
                    {icon}
                  </span>
                  <span className="nb__grouplabel">{label}</span>
                  {unreadInCat > 0 ? <span className="nb__groupcount">{unreadInCat}</span> : null}
                </div>
                <ul>
                  {items.map((n) => (
                    <li key={n.id} className={n.read ? '' : 'is-unread'}>
                      <button type="button" onClick={() => go(n)}>
                        <span className="nb__body">
                          <span className="nb__text">{n.text}</span>
                          <small>{ago(n.createdAt)}</small>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="nb__dismiss"
                        aria-label={t('eliminaNotificarea')}
                        title={t('elimina')}
                        onClick={() => dismiss.mutate(n.id)}
                      >
                        <X size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}

          <div className="nb__prefs">
            <button
              type="button"
              className={`nb__pref${muted ? '' : ' is-on'}`}
              aria-pressed={!muted}
              title={muted ? t('pornesteSunetulLaNotificari') : t('opresteSunetulLaNotificari')}
              onClick={() => {
                const next = !muted;
                setMuted(next);
                writeMuted(next);
                if (!next) playChime(); // o mostră când îl pornești
              }}
            >
              {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
              {muted ? t('sunetOprit') : t('sunetPornit')}
            </button>

            {osPerm === 'default' ? (
              <button
                type="button"
                className="nb__pref"
                title={t('arataNotificarileSiPe')}
                onClick={() => {
                  void Notification.requestPermission().then((p) => setOsPerm(p));
                }}
              >
                <BellRing size={13} /> {t('alertePeDesktop')}
              </button>
            ) : osPerm === 'granted' ? (
              <span className="nb__prefnote is-on">
                <BellRing size={12} /> {t('alertePeDesktopPornite')}
              </span>
            ) : osPerm === 'denied' ? (
              <span className="nb__prefnote">{t('alertelePeDesktopSunt')}</span>
            ) : null}

            <button
              type="button"
              className={`nb__pref${quiet.on ? ' is-on' : ''}`}
              aria-pressed={quiet.on}
              title={t('intrUnIntervalNu')}
              onClick={() => {
                const next = { ...quiet, on: !quiet.on };
                setQuiet(next);
                writeQuiet(next);
              }}
            >
              <Moon size={13} /> {t('oreDeLiniste')}
            </button>

            {quiet.on ? (
              <span className="nb__quiet">
                <input
                  type="time"
                  value={quiet.from}
                  aria-label={t('linisteDeLa')}
                  onChange={(e) => {
                    const next = { ...quiet, from: e.target.value || quiet.from };
                    setQuiet(next);
                    writeQuiet(next);
                  }}
                />
                <span aria-hidden>–</span>
                <input
                  type="time"
                  value={quiet.to}
                  aria-label={t('linistePanaLa')}
                  onChange={(e) => {
                    const next = { ...quiet, to: e.target.value || quiet.to };
                    setQuiet(next);
                    writeQuiet(next);
                  }}
                />
              </span>
            ) : null}
          </div>

          {publicKey && perm !== 'unsupported' ? (
            <footer className="nb__push">
              {perm === 'denied' ? (
                <span className="nb__pushnote">{t('notificarileInBrowserSunt')}</span>
              ) : subscribed ? (
                <>
                  <span className="nb__pushnote is-on">
                    <BellRing size={12} /> {t('notificariInBrowserPornite')}
                  </span>
                  <button
                    type="button"
                    onClick={() => test.mutate()}
                    disabled={test.isPending}
                    title={t('trimiteUnPushDe')}
                  >
                    {test.isPending ? t('seTrimite') : t('testeaza')}
                  </button>
                  <button
                    type="button"
                    onClick={() => disable.mutate()}
                    disabled={disable.isPending}
                  >
                    {t('opreste')}
                  </button>
                  {testNote ? <span className="nb__pushnote">{testNote}</span> : null}
                </>
              ) : (
                <button
                  type="button"
                  className="nb__pushbtn"
                  onClick={() => enable.mutate()}
                  disabled={enable.isPending}
                >
                  <BellRing size={12} /> {t('primesteNotificariSiCand')}
                </button>
              )}
            </footer>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
